/**
 * Dashboard admin — Sprint 12 redesign.
 *
 * Layout idéntico al mockup del hero de la landing (coherencia
 * comercial ↔ producto). 4 KPIs principales:
 *   - Auditorías activas (in_progress)
 *   - Evidencias capturadas (count photos)
 *   - Hallazgos abiertos (proxy de respuestas "No"/"No cumple")
 *   - Cumplimiento medio (avg_global_score)
 *
 * Panel inferior: levantamientos recientes con badges por categoría +
 * cumplimiento por dominio (lista de ScoreBars).
 */
import { type FormEvent, useEffect, useState } from 'react';

import { categoryForCode, CATEGORY_META, type TemplateCategory } from '@assessos/shared';

import { type DashboardData, adminDashboardApi } from '../api/admin-dashboard.js';
import {
  Badge,
  ButtonLink,
  Card,
  EmptyState,
  LoadingSkeleton,
  PageHeader,
  ScoreBar,
  StatCard,
  scoreTextColor,
} from '../components/ui.js';

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function CATEGORY_TONE(cat: TemplateCategory): 'brand' | 'emerald' | 'amber' | 'purple' | 'slate' {
  switch (cat) {
    case 'physical_security':
      return 'brand';
    case 'cybersecurity':
      return 'emerald';
    case 'regulatory_rd':
      return 'amber';
    case 'customs_food':
      return 'purple';
    default:
      return 'slate';
  }
}

export function AdminDashboardPage(): JSX.Element {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const [from, setFrom] = useState(isoDate(monthAgo));
  const [to, setTo] = useState(isoDate(today));
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load(f: string, t: string): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const r = await adminDashboardApi.get({
        from: new Date(`${f}T00:00:00Z`).toISOString(),
        to: new Date(`${t}T23:59:59Z`).toISOString(),
      });
      setData(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(from, to);
  }, []);

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    void load(from, to);
  }

  return (
    <main className="mx-auto max-w-7xl px-6 py-8 space-y-6">
      <PageHeader
        eyebrow="DASHBOARD"
        title="Resumen de auditorías activas"
        subtitle="KPIs sobre el rango seleccionado. Por defecto últimos 30 días."
        actions={
          <ButtonLink to="/admin/billing" variant="secondary" size="md">
            Cobranza →
          </ButtonLink>
        }
      />

      {/* Filtros */}
      <form
        onSubmit={handleSubmit}
        className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-4"
      >
        <div className="flex flex-col gap-1">
          <label htmlFor="from" className="text-xs font-semibold text-slate-700">
            Desde
          </label>
          <input
            id="from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="to" className="text-xs font-semibold text-slate-700">
            Hasta
          </label>
          <input
            id="to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700/90 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {loading ? 'Cargando…' : 'Aplicar'}
        </button>
        <p className="ml-auto text-xs text-slate-500">
          {from} → {to}
        </p>
      </form>

      {error && (
        <div
          role="alert"
          className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700"
        >
          {error}
        </div>
      )}

      {loading && !data && <LoadingSkeleton rows={3} />}

      {data && (
        <>
          {/* 4 KPIs — mismo orden y framing que el mockup del landing */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="AUDITORÍAS ACTIVAS"
              value={String(data.assessments.in_progress)}
              delta={`${data.assessments.completed} cerradas en el rango`}
              tone="brand"
            />
            <StatCard
              label="EVIDENCIAS CAPTURADAS"
              value={data.evidences_captured.toLocaleString('es-DO')}
              delta="Fotos georreferenciadas en el rango"
              tone="emerald"
            />
            <StatCard
              label="HALLAZGOS ABIERTOS"
              value={String(data.open_findings)}
              delta="Hallazgos con estado abierto o en curso"
              tone="rose"
            />
            <StatCard
              label="CUMPLIMIENTO MEDIO"
              value={data.avg_global_score !== null ? `${data.avg_global_score.toFixed(0)}%` : '—'}
              delta={data.avg_global_score !== null ? 'Score ponderado promedio' : 'Sin datos'}
              tone={
                data.avg_global_score === null
                  ? 'slate'
                  : data.avg_global_score >= 75
                    ? 'emerald'
                    : data.avg_global_score >= 50
                      ? 'amber'
                      : 'rose'
              }
            />
          </section>

          {/* Panel: levantamientos recientes + cumplimiento por dominio */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card
              title="Levantamientos recientes"
              hint="Últimos en el rango con su categoría y auditor"
              className="lg:col-span-2"
            >
              {data.top_risk_clients.length === 0 ? (
                <EmptyState
                  title="Sin levantamientos en el rango"
                  description="Cuando tu equipo cierre el primer levantamiento aparecerá aquí con su score y categoría."
                />
              ) : (
                <ul className="space-y-2">
                  {data.top_risk_clients.map((c) => (
                    <li
                      key={c.id}
                      className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-sm font-bold text-brand-700 border border-slate-200">
                          {c.name
                            .split(/\s+/)
                            .map((p) => p[0])
                            .filter(Boolean)
                            .slice(0, 2)
                            .join('')
                            .toUpperCase()}
                        </span>
                        <div className="min-w-0">
                          <p className="font-semibold text-brand-900 truncate">{c.name}</p>
                          <p className="text-xs text-slate-500">
                            {c.sector ?? 'sin sector'} · {c.assessments} levantamientos
                          </p>
                        </div>
                      </div>
                      <span className={`text-sm font-bold ${scoreTextColor(c.average_score)}`}>
                        {c.average_score.toFixed(0)}%
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card title="Top auditores" hint="Por cierres en el rango">
              {data.top_auditors.length === 0 ? (
                <EmptyState
                  title="Sin actividad"
                  description="Sin auditores activos en el rango."
                />
              ) : (
                <ol className="space-y-2">
                  {data.top_auditors.map((a, idx) => (
                    <li key={a.email} className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-[11px] font-bold text-brand-700">
                          {idx + 1}
                        </span>
                        <span className="font-medium text-slate-700 text-sm truncate">
                          {a.email}
                        </span>
                      </span>
                      <Badge tone="emerald">{a.completed}</Badge>
                    </li>
                  ))}
                </ol>
              )}
            </Card>
          </div>

          {/* Cumplimiento por dominio (full width) */}
          <Card
            title="Cumplimiento por dominio"
            hint={`${data.avg_per_domain.length} dominios evaluados`}
          >
            {data.avg_per_domain.length === 0 ? (
              <EmptyState
                title="Sin scores por dominio"
                description="Cuando se cierren levantamientos con scoring, aparecerán aquí agrupados por categoría."
              />
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                {data.avg_per_domain.map((d) => {
                  const cat = categoryForCode(d.template_code);
                  const meta = CATEGORY_META[cat];
                  return (
                    <li key={`${d.template_code}-${d.domain}`} className="space-y-1">
                      <div className="flex items-baseline gap-2 text-xs">
                        <Badge tone={CATEGORY_TONE(cat)}>{meta.short}</Badge>
                        <span className="font-medium text-slate-700 truncate">
                          {d.template_code.toUpperCase()} · {d.domain}
                        </span>
                        <span className="ml-auto text-slate-400">{d.samples}</span>
                      </div>
                      <ScoreBar label="" score={d.average} />
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </>
      )}
    </main>
  );
}
